import { Injectable, inject } from '@angular/core';
import { map, Observable, switchMap, tap } from 'rxjs';
import {
  ConfirmationService,
  DateFormatService,
  FileSaverService,
  TableDataPayload,
  TableLazyLoad,
  ToastService,
} from '@web/shared/ui';
import { CollectionRequestBuilder, YYYY_MM_DD_HH_MM_SS } from '@web/common';
import { AdminInvoiceApiService } from '../api/services/admin-invoice-api.service';
import { AdminInvoiceCollectionFilter } from '../models/admin-invoice-collection-filter';
import { AdminInvoiceCollectionItem } from '../models/admin-invoice-collection-item';
import { AdminInvoiceSettingsFormData } from '../models/admin-invoice-settings-form-data';
import { AdminInvoiceSettingsUpdateRequest } from '../api/requests/admin-invoice-settings-update.request';
import { Guid, once, valueOrDefault } from '@web/utils';

@Injectable({
  providedIn: 'root',
})
export class AdminInvoiceDataSourceService {
  private readonly invoiceApi = inject(AdminInvoiceApiService);
  private readonly collectionRequestBuilder = inject(CollectionRequestBuilder);
  private readonly confirmation = inject(ConfirmationService);
  private readonly dateFormat = inject(DateFormatService);
  private readonly fileSaver = inject(FileSaverService);
  private readonly toast = inject(ToastService);

  public fetchCollection(
    lazyLoad: TableLazyLoad<AdminInvoiceCollectionFilter>,
  ): Observable<TableDataPayload<AdminInvoiceCollectionItem>> {
    /* prettier-ignore */
    return this.invoiceApi
      .fetchCollection(
        {
          request: this.collectionRequestBuilder
            .page(lazyLoad.page)
            .pageSize(lazyLoad.rows)
            .sorts((builder) =>
              builder.fromTableSort(lazyLoad.sort).buildAsArray(),
            )
            .filters((builder) =>
              builder
                .fromTableFilter(lazyLoad.filter)
                .appendDate((builder) => builder.rangeFor('createdAt').build())
                .appendText((builder) => builder.caseInsensitiveLikeFor('number').build())
                .appendText((builder) => builder.inFor('status').build())
                .appendText((builder) => builder.inFor('ksefStatus').build())
                .appendText((builder) => builder.inFor('type').build())
                .appendText((builder) => builder.equalFor('hasError').build())
                .appendText((builder) => builder.caseInsensitiveLikeFor('taskReferenceNumber').build())
                .appendText((builder) => builder.caseInsensitiveLikeFor('ksefNumber').build())
                .buildAsArray(),
            )
            .build()
        },
      )
      .pipe(
        map(
          (response) => new TableDataPayload(
            response.items.map(
              (item) =>
                /* prettier-ignore */
                new AdminInvoiceCollectionItem({
                  id: item.id,
                  canBeDownloaded: item.contextOperations.canBeDownloaded,
                  canBeSendToKsef: item.contextOperations.canBeSendToKsef,
                  canBeSynchronized: item.contextOperations.canBeSynchronized,
                  status: item.status,
                  ksefStatus: item.ksefStatus,
                  number: item.number,
                  createdAt: item.createdAt,
                  type: item.type,
                  hasError: valueOrDefault(item.hasError, false),
                  errorMessage: item.errorMessage,
                  taskReferenceNumber: item.taskReferenceNumber,
                  ksefNumber: item.ksefNumber,
                }),
            ),
            response.pageSize,
            response.page,
            response.total,
          )
        ),
      );
  }

  public fetchSettingsFormData(): Observable<AdminInvoiceSettingsFormData> {
    return this.invoiceApi.fetchSettings({ skipByLoading: true }).pipe(
      map(
        (response) =>
          new AdminInvoiceSettingsFormData({
            isConnectedWithKsef: response.isConnectedWithKsef,
            sendToKsef: response.sendToKsef,
            seller: response.seller,
            temporaryInvoiceBankName: response.temporaryInvoiceBankName,
            temporaryInvoiceAccount: response.temporaryInvoiceAccount,
          }),
      ),
    );
  }

  public updateSettingsByFormData(
    data: AdminInvoiceSettingsFormData,
  ): Observable<void> {
    return this.invoiceApi
      .updateSettings(
        new AdminInvoiceSettingsUpdateRequest({
          sendToKsef: data.sendToKsef,
          seller: data.seller,
          temporaryInvoiceBankName: data.temporaryInvoiceBankName,
          temporaryInvoiceAccount: data.temporaryInvoiceAccount,
        }),
      )
      .pipe(
        tap(() => {
          this.toast.show((builder) =>
            builder
              .initSuccess('Konfiguracja faktur została zaktualizowana.')
              .build(),
          );
        }),
      );
  }

  public downloadOrderInvoiceById(id: Guid): Observable<void> {
    const invoiceFileName = `faktura_${id}_${this.dateFormat.format(new Date(), YYYY_MM_DD_HH_MM_SS)}`;

    return this.invoiceApi
      .downloadOrderInvoiceById(id)
      .pipe(switchMap((blob) => this.fileSaver.saveAs(blob, invoiceFileName)));
  }

  public downloadRefundInvoiceById(id: Guid): Observable<void> {
    const invoiceFileName = `faktura_korygujaca_${id}_${this.dateFormat.format(new Date(), YYYY_MM_DD_HH_MM_SS)}`;

    return this.invoiceApi
      .downloadRefundInvoiceById(id)
      .pipe(switchMap((blob) => this.fileSaver.saveAs(blob, invoiceFileName)));
  }

  public confirmAndSynchronizeInvoiceById(id: Guid): Observable<void> {
    return new Observable<void>((subscriber) => {
      this.confirmation.show((builder) =>
        builder
          .title('Potwierdzenie')
          .message('Czy na pewno chcesz zsynchronizować dane faktury?')
          .accept(() => {
            this.invoiceApi
              .synchronizeInvoiceById(id)
              .pipe(
                once(),
                tap(() =>
                  this.toast.show((builder) =>
                    builder
                      .initSuccess(
                        'Uruchomiono proces synnchronizacji danych faktury.',
                      )
                      .build(),
                  ),
                ),
              )
              .subscribe(subscriber);
          })
          .build(),
      );
    });
  }

  public confirmAndSendToKsefById(id: Guid): Observable<void> {
    return new Observable<void>((subscriber) => {
      this.confirmation.show((builder) =>
        builder
          .title('Potwierdzenie')
          .message('Czy na pewno chcesz wysłać wybraną fakturę do KSeF?')
          .accept(() => {
            this.invoiceApi
              .sendToKsefById(id)
              .pipe(
                once(),
                tap(() =>
                  this.toast.show((builder) =>
                    builder
                      .initSuccess(
                        'Uruchomiono proces wysyłki faktury do KSeF.',
                      )
                      .build(),
                  ),
                ),
              )
              .subscribe(subscriber);
          })
          .build(),
      );
    });
  }
}
